import React from 'react';
import { Form, Button, Tooltip, Divider } from 'antd';

interface IQuoteActionButtonsProps {
    isSafeToCompleteQuote: boolean;
    hasMixedQuoteItems: boolean;
    loading: boolean;
    disableSubmit?: boolean;
    /** True while any part is still analyzing; its price is provisional until then. */
    isAnalyzing: boolean;
    setSubmitAndMarkAsCompleted: (action: 'rfq' | 'complete' | 'checkout') => void;
    buyNowCount: number;
    manualQuoteCount: number;
    onSaveDraft: () => void;
    onGetHelp: () => void;
    isStaffUser: boolean;
    isUserLoggedIn: boolean;
}

const QuoteActionButtons: React.FC<IQuoteActionButtonsProps> = ({
    isSafeToCompleteQuote,
    hasMixedQuoteItems,
    loading,
    disableSubmit = false,
    isAnalyzing,
    setSubmitAndMarkAsCompleted,
    buyNowCount,
    manualQuoteCount,
    onSaveDraft,
    onGetHelp,
    isStaffUser,
    isUserLoggedIn,
}) => {
    const allPriced = isSafeToCompleteQuote;
    const mixedPriced = hasMixedQuoteItems;
    const nonePriced = !allPriced && !mixedPriced;

    const showCheckout = allPriced && !isStaffUser;
    const showComplete = allPriced;
    const showPurchaseAndRFQ = mixedPriced && !isStaffUser;
    const showRequestQuote = nonePriced || mixedPriced;

    const ActionButton = ({
        id,
        testId,
        label,
        description,
        type = 'primary',
        onClick,
    }: {
        id: string;
        testId: string;
        label: string;
        description: React.ReactNode;
        type?: 'primary' | 'default';
        onClick: () => void;
    }) => (
        <Form.Item shouldUpdate noStyle>
            {({ getFieldValue }) => (
                <div className="mb-3">
                    <Button
                        id={id}
                        data-testid={testId}
                        block
                        size="large"
                        type={type}
                        loading={loading}
                        disabled={disableSubmit || isAnalyzing}
                        onClick={onClick}
                    >
                        {label}
                    </Button>
                    <p className="text-[11px] text-slate-500 text-center mt-1">{description}</p>
                </div>
            )}
        </Form.Item>
    );

    return (
        <div data-testid="quote-action-buttons">
            {isAnalyzing && (
                <p className="text-xs text-slate-500 text-center mb-3">
                    Prices will be available once your files finish analyzing.
                </p>
            )}

            {/* Checkout */}
            {showCheckout && (
                <ActionButton
                    id="checkout"
                    testId="quote-checkout-btn"
                    label="Checkout"
                    description={
                        <>
                            Click <strong>“Checkout”</strong> to review your order details and proceed with placing your
                            order based on this quote. You will be guided through the final steps to complete your
                            purchase.
                        </>
                    }
                    onClick={() => setSubmitAndMarkAsCompleted('checkout')}
                />
            )}

            {/* Complete */}
            {showComplete && (
                <ActionButton
                    id="complete"
                    testId="quote-complete-btn"
                    label="Complete"
                    type={isStaffUser ? 'primary' : 'default'}
                    description={
                        <>
                            When you click <strong>“Complete”</strong>, the quote will be marked as completed. Completed
                            quotes can be accessed at any time, exported as a PDF, and used to place an order whenever
                            needed.
                        </>
                    }
                    onClick={() => setSubmitAndMarkAsCompleted('complete')}
                />
            )}

            {/* Purchase & Request Quote */}
            {showPurchaseAndRFQ && (
                <ActionButton
                    id="purchase-and-request-quote"
                    testId="quote-purchase-and-request-quote-btn"
                    label="Continue Purchase & Request Quote"
                    description={
                        <>
                            Click <strong>“Purchase & Request Quote”</strong> to continue with the items that are ready
                            to purchase and submit the remaining items to our Design Engineers for a formal quote. You
                            will be checking out {buyNowCount} item
                            {buyNowCount !== 1 ? 's' : ''}, and {manualQuoteCount} item
                            {manualQuoteCount !== 1 ? 's' : ''} will be submitted for RFQ.
                        </>
                    }
                    onClick={() => setSubmitAndMarkAsCompleted('checkout')}
                />
            )}

            {/* Request Quote */}
            {showRequestQuote && (
                <ActionButton
                    id="request-quote"
                    testId="quote-request-quote-btn"
                    label={!hasMixedQuoteItems ? 'Request Quote' : 'Request Quote (All Items)'}
                    type={!hasMixedQuoteItems ? 'primary' : 'default'}
                    description={
                        <>
                            Click <strong>“Request Quote”</strong> to submit all items for review. Our Design Engineers
                            will evaluate your request and prepare a formal quote, which will be sent to you afterward.
                        </>
                    }
                    onClick={() => setSubmitAndMarkAsCompleted('rfq')}
                />
            )}

            {/* Save as Draft – always visible */}
            <Tooltip title="Save your progress without completing the quote. Draft quotes can be updated at any time.">
                <Button data-testid="quote-save-draft-btn" type="link" block className="mt-2" onClick={onSaveDraft}>
                    Save as Draft
                </Button>
            </Tooltip>

            <Divider />

            {/* Help */}
            {isUserLoggedIn && (
                <div className="text-center">
                    <p className="text-xs text-slate-500">Questions? Contact your Sales Representative.</p>
                    <Button type="link" className="text-xs text-blue-600 pt-0 h-auto" onClick={onGetHelp}>
                        Get Help
                    </Button>
                </div>
            )}
        </div>
    );
};

export default QuoteActionButtons;
